/**
 * Currency helpers driven by Admin > Settings > General > Primary Currency.
 *
 * The setting is stored as a display label like "NGN - Naira" or
 * "USD - US Dollar" (see GeneralPanel.jsx). We parse the ISO code out of that
 * label and use it to pick a symbol + Intl.NumberFormat locale so every price
 * in the app renders consistently with whatever an admin configures, instead
 * of each screen hardcoding "₦" or "N" on its own.
 */

const CURRENCY_META = {
  NGN: { symbol: "₦", locale: "en-NG" },
  USD: { symbol: "$", locale: "en-US" },
  GBP: { symbol: "£", locale: "en-GB" },
  EUR: { symbol: "€", locale: "en-IE" },
  GHS: { symbol: "GH₵", locale: "en-GH" },
  KES: { symbol: "KSh", locale: "en-KE" },
  ZAR: { symbol: "R", locale: "en-ZA" },
};

const DEFAULT_CODE = "NGN";

/**
 * Extracts the ISO currency code from a "CODE - Label" string.
 * Falls back to NGN if the value is missing or unrecognized.
 */
export function getCurrencyCode(primaryCurrency) {
  if (!primaryCurrency || typeof primaryCurrency !== "string") {
    return DEFAULT_CODE;
  }
  const code = primaryCurrency.split("-")[0].trim().toUpperCase();
  return CURRENCY_META[code] ? code : DEFAULT_CODE;
}

export function getCurrencySymbol(primaryCurrency) {
  const code = getCurrencyCode(primaryCurrency);
  return CURRENCY_META[code].symbol;
}

/**
 * Formats a numeric amount using the platform's configured primary currency.
 * @param {number} amount
 * @param {string} primaryCurrency - e.g. settings.platformConfiguration.primaryCurrency
 * @param {object} [options] - passed through to Intl.NumberFormat
 */
export function formatCurrency(amount, primaryCurrency, options = {}) {
  const code = getCurrencyCode(primaryCurrency);
  const meta = CURRENCY_META[code];
  const numericAmount = Number(amount) || 0;

  try {
    return new Intl.NumberFormat(meta.locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
      ...options,
    }).format(numericAmount);
  } catch {
    // Fall back to a manual symbol + toLocaleString if Intl doesn't
    // recognize the currency code for some reason.
    return `${meta.symbol}${numericAmount.toLocaleString()}`;
  }
}

export default formatCurrency;
